import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mutate, uploadFile, api } from '../shared/api';
import { readUrlParam, useUrlSync } from '../shared/urlState';
import type { Filters, FoundationPayload } from '../types/biApp';

type CategoryRow = {
    id: number;
    company_id: number;
    parent_id: number | null;
    name: string;
    sort_order: number;
    doc_count: number;
};
type DocumentRow = {
    id: number;
    company_id: number;
    category_id: number | null;
    category_name: string | null;
    title: string;
    original_filename: string;
    mime_type: string | null;
    size_bytes: number;
    docdoc_url: string | null;
    docdoc_document_uuid: string | null;
    docdoc_view_url: string | null;
    status: string;
    ai_summary: string | null;
    processing_error: string | null;
    created_at: string | null;
    updated_at: string | null;
    is_circular?: boolean;
    source_circular_id?: number | null;
    circular_number?: string | null;
    attachment_number?: string | null;
    approved_by?: string | null;
    approved_at?: string | null;
};
type SearchHit = {
    id: number;
    company_id: number;
    company_code: string | null;
    company_name: string;
    title: string;
    original_filename: string;
    mime_type: string | null;
    category_name: string | null;
    snippet: string | null;
    status: string;
    has_docdoc: boolean;
    created_at: string | null;
};

type SortKey = 'title' | 'category' | 'status' | 'created_at';

export function DocumentationModule({
    token,
    locale,
    foundation,
    filters,
}: {
    token: string;
    locale: 'hr' | 'en';
    foundation: FoundationPayload;
    filters: Filters;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const accessibleCompanies = foundation.companies;
    const [companyId, setCompanyId] = useState<number>(
        filters.companyIds[0] ?? accessibleCompanies[0]?.id ?? 0,
    );

    const [tab, setTab] = useState<'docs' | 'search'>(readUrlParam('tab', 'docs') as 'docs' | 'search');
    useUrlSync('tab', tab, tab === 'docs');
    const [categories, setCategories] = useState<CategoryRow[]>([]);
    const [documents, setDocuments] = useState<DocumentRow[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<number | 'all' | 'uncategorized'>('all');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('created_at');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    // Modals
    const [showCategoriesModal, setShowCategoriesModal] = useState(false);
    const [showNewDocModal, setShowNewDocModal] = useState(false);
    const [editDoc, setEditDoc] = useState<DocumentRow | null>(null);
    const [summaryDoc, setSummaryDoc] = useState<DocumentRow | null>(null);
    const [circularPreview, setCircularPreview] = useState<DocumentRow | null>(null);

    // AI search
    const [searchQ, setSearchQ] = useState('');
    const [searchHits, setSearchHits] = useState<SearchHit[]>([]);
    const [searchBusy, setSearchBusy] = useState(false);

    const load = useCallback(async () => {
        if (!companyId) return;
        setError('');
        setLoading(true);
        try {
            const params = new URLSearchParams({ company_id: String(companyId) });
            if (selectedCategory !== 'all') params.set('category_id', String(selectedCategory));
            const [c, d] = await Promise.all([
                api<{ rows: CategoryRow[] }>(`/documentation/categories?company_id=${companyId}`, token),
                api<{ rows: DocumentRow[] }>(`/documentation/documents?${params.toString()}`, token),
            ]);
            setCategories(c.rows);
            setDocuments(d.rows);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, companyId, selectedCategory]);

    useEffect(() => { void load(); }, [load]);
    useEffect(() => { setSelectedIds(new Set()); }, [companyId, selectedCategory]);

    // Auto-osvježavanje dok ima pending/processing dokumenata — provjera svakih 4s,
    // prekid kad su svi gotovi (ready/failed) ili kad user promijeni tab/firmu.
    useEffect(() => {
        if (tab !== 'docs') return;
        const hasPending = documents.some((d) => d.status === 'pending' || d.status === 'processing');
        if (! hasPending) return;
        const interval = setInterval(() => { void load(); }, 4000);
        return () => clearInterval(interval);
    }, [tab, documents, load]);

    const activeCompany = accessibleCompanies.find((c) => c.id === companyId);

    // Filter + sort documents
    const filteredDocs = useMemo(() => {
        const q = search.trim().toLowerCase();
        let list = documents.filter((d) => {
            if (! q) return true;
            return d.title.toLowerCase().includes(q)
                || d.original_filename.toLowerCase().includes(q)
                || (d.category_name ?? '').toLowerCase().includes(q)
                || (d.ai_summary ?? '').toLowerCase().includes(q);
        });
        list = [...list].sort((a, b) => {
            const dir = sortDir === 'asc' ? 1 : -1;
            const aVal = (sortKey === 'title' ? a.title.toLowerCase()
                : sortKey === 'category' ? (a.category_name ?? '').toLowerCase()
                : sortKey === 'status' ? a.status.toLowerCase()
                : (a.created_at ?? '')) as string;
            const bVal = (sortKey === 'title' ? b.title.toLowerCase()
                : sortKey === 'category' ? (b.category_name ?? '').toLowerCase()
                : sortKey === 'status' ? b.status.toLowerCase()
                : (b.created_at ?? '')) as string;
            if (aVal < bVal) return -1 * dir;
            if (aVal > bVal) return 1 * dir;
            return 0;
        });
        return list;
    }, [documents, search, sortKey, sortDir]);

    function toggleSort(k: SortKey) {
        if (sortKey === k) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(k);
            setSortDir(k === 'created_at' ? 'desc' : 'asc');
        }
    }

    function toggleSelected(id: number) {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedIds(next);
    }

    function selectAllVisible() {
        if (selectedIds.size === filteredDocs.length && filteredDocs.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredDocs.map((d) => d.id)));
        }
    }

    async function viewDoc(id: number) {
        try {
            const res = await fetch(`/api/documentation/documents/${id}/view`, { headers: { Authorization: `Bearer ${token}` } });
            if (! res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) { setError(err instanceof Error ? err.message : 'Pregled fail'); }
    }

    // Okružnice: view-only PNG preview (poštuje "ne skidaj"); ostali dokumenti: blob iz DocDoc-a.
    function openDoc(d: DocumentRow) {
        if (d.is_circular) { setCircularPreview(d); return; }
        void viewDoc(d.id);
    }

    async function deleteDoc(id: number) {
        if (! confirm(t('Obrisati ovaj dokument? Soft-delete — vraća se iz "Arhivirani" do 20 dana.', 'Delete this document? Soft-delete — recoverable within 20 days.'))) return;
        const alsoDocdoc = confirm(t('Obrisati i s DocDoc-a TRAJNO?', 'Also delete from DocDoc PERMANENTLY?'));
        try {
            await api(`/documentation/documents/${id}?delete_from_docdoc=${alsoDocdoc ? '1' : '0'}`, token, { method: 'DELETE' });
            await load();
        } catch (err) { setError(err instanceof Error ? err.message : ''); }
    }

    async function batchSetCategory(categoryId: number | null) {
        if (selectedIds.size === 0) return;
        try {
            await api('/documentation/documents/batch', token, { method: 'POST', body: JSON.stringify({
                ids: Array.from(selectedIds),
                action: 'set_category',
                category_id: categoryId,
            })});
            setSelectedIds(new Set());
            await load();
        } catch (err) { setError(err instanceof Error ? err.message : ''); }
    }

    async function batchDelete() {
        if (selectedIds.size === 0) return;
        if (! confirm(t(`Obrisati ${selectedIds.size} odabranih dokumenata?`, `Delete ${selectedIds.size} selected documents?`))) return;
        try {
            await api('/documentation/documents/batch', token, { method: 'POST', body: JSON.stringify({
                ids: Array.from(selectedIds),
                action: 'soft_delete',
            })});
            setSelectedIds(new Set());
            await load();
        } catch (err) { setError(err instanceof Error ? err.message : ''); }
    }

    async function runSearch() {
        if (searchQ.trim().length < 2) return;
        setSearchBusy(true); setError('');
        try {
            const r = await api<{ rows: SearchHit[] }>(`/documentation/search?q=${encodeURIComponent(searchQ.trim())}`, token);
            setSearchHits(r.rows);
        } catch (err) { setError(err instanceof Error ? err.message : ''); }
        finally { setSearchBusy(false); }
    }

    return (
        <section className="card">
            {/* Hero */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.2rem', marginBottom: '1rem' }}>
                <div style={{
                    fontSize: '4.6rem', lineHeight: 1, fontWeight: 800, color: 'var(--ad-acc)',
                    background: 'linear-gradient(135deg, var(--ad-acc), var(--ad-ink))',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text', minWidth: 90,
                }}>31</div>
                <div style={{ flex: 1 }}>
                    <h1 style={{ margin: 0, fontSize: '2.2rem', letterSpacing: '-0.01em', color: 'var(--ad-ink)' }}>
                        {t('DOKUMENTACIJA (DMS)', 'DOCUMENTATION (DMS)')}
                    </h1>
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.92rem' }}>
                        {t('DocDoc gateway · AI sažetak u bazu znanja · per-firma.', 'DocDoc gateway · AI summary in knowledge base · per-company.')}
                    </p>
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '0.78rem', color: 'var(--ad-ink)', fontWeight: 600, letterSpacing: '0.04em' }}>{t('TVRTKA', 'COMPANY')}</label>
                        <select
                            value={companyId}
                            onChange={(e) => setCompanyId(Number(e.target.value))}
                            style={{
                                padding: '8px 14px',
                                borderRadius: 10,
                                border: '1.5px solid rgba(var(--ad-soft-rgb),0.45)',
                                background: 'linear-gradient(135deg, #fffbeb, rgba(254,243,199,0.5))',
                                color: 'var(--ad-ink)',
                                fontWeight: 600,
                                fontSize: '0.92rem',
                                minWidth: 240,
                                cursor: 'pointer',
                            }}
                        >
                            {accessibleCompanies.map((c) => (
                                <option key={c.id} value={c.id}>{c.code ? `${c.code} · ${c.name}` : c.name}</option>
                            ))}
                        </select>
                        <span className="muted" style={{ fontSize: '0.76rem' }}>· {accessibleCompanies.length} {t('tvrtki dostupno', 'companies available')}</span>
                    </div>
                </div>
            </div>

            {error ? <p className="error">{error}</p> : null}

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: '1rem', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.2)', paddingBottom: 6 }}>
                <button type="button" onClick={() => setTab('docs')} style={{
                    background: tab === 'docs' ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                    color: tab === 'docs' ? '#fffbeb' : 'var(--ad-ink)',
                    border: tab === 'docs' ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                    padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem',
                }}>📂 {t('Dokumenti', 'Documents')} {activeCompany ? `· ${activeCompany.code ?? activeCompany.name}` : ''}</button>
                <button type="button" onClick={() => setTab('search')} style={{
                    background: tab === 'search' ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                    color: tab === 'search' ? '#fffbeb' : 'var(--ad-ink)',
                    border: tab === 'search' ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                    padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem',
                }}>🔍 {t('AI pretraga (sve moje tvrtke)', 'AI search (all my companies)')}</button>
            </div>

            {tab === 'docs' && !companyId ? <p className="muted">{t('Odaberi tvrtku.', 'Pick a company.')}</p> : null}

            {tab === 'docs' && companyId ? (
                <>
                    {/* Toolbar */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.8rem' }}>
                        <button type="button" className="compact" onClick={() => setShowNewDocModal(true)} style={{
                            background: 'linear-gradient(135deg, var(--ad-acc), #b8895a)', color: '#fff', border: 0,
                            padding: '8px 14px', borderRadius: 8, fontWeight: 600,
                        }}>📤 {t('Novi dokument', 'New document')}</button>
                        <button type="button" className="secondary compact" onClick={() => setShowCategoriesModal(true)}>📁 {t('Kategorije', 'Categories')} ({categories.length})</button>
                        <button type="button" className="secondary compact" disabled={loading} onClick={() => void load()} title={t('Osvježi pregled dokumenata bez reload-a stranice', 'Reload documents list')}>{loading ? '⏳' : '🔄'} {t('Osvježi', 'Refresh')}</button>
                        <div style={{ flex: 1, minWidth: 200 }}>
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('Brza pretraga (naslov, datoteka, kategorija, sažetak)…', 'Quick search…')}
                                style={{ width: '100%', padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.35)' }}
                            />
                        </div>
                    </div>

                    {/* Batch toolbar */}
                    {selectedIds.size > 0 ? (
                        <div style={{
                            display: 'flex', gap: 8, alignItems: 'center', padding: '0.6rem 1rem', borderRadius: 10,
                            background: 'linear-gradient(135deg, #fef3c7, rgba(254,243,199,0.5))',
                            border: '1px solid rgba(var(--ad-acc-rgb),0.35)', marginBottom: '0.8rem', flexWrap: 'wrap',
                        }}>
                            <strong style={{ color: 'var(--ad-ink)' }}>{selectedIds.size} {t('odabrano', 'selected')}</strong>
                            <select
                                onChange={(e) => { const v = e.target.value; if (v) { void batchSetCategory(v === 'none' ? null : Number(v)); e.currentTarget.value = ''; } }}
                                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }}
                            >
                                <option value="">{t('Premjesti u kategoriju…', 'Move to category…')}</option>
                                <option value="none">{t('— Bez kategorije', '— No category')}</option>
                                {categories.map((c) => <option key={c.id} value={c.id}>{categoryPathLabel(categories, c.id)}</option>)}
                            </select>
                            <button type="button" className="secondary compact" style={{ color: '#b14040' }} onClick={() => void batchDelete()}>🗑 {t('Obriši odabrano', 'Delete selected')}</button>
                            <button type="button" className="secondary compact" onClick={() => setSelectedIds(new Set())}>{t('Poništi odabir', 'Clear')}</button>
                        </div>
                    ) : null}

                    {/* Two-column: tree + table */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: '1rem', alignItems: 'start' }}>
                        <CategoryTreePanel
                            categories={categories}
                            selected={selectedCategory}
                            onSelect={setSelectedCategory}
                            locale={locale}
                            docsAllCount={documents.length}
                        />
                        <div>
                            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
                            <table className="admin-user-table" style={{ width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: 28 }}>
                                            <input type="checkbox"
                                                checked={filteredDocs.length > 0 && selectedIds.size === filteredDocs.length}
                                                onChange={selectAllVisible} />
                                        </th>
                                        <SortHeader k="title" label={t('Naslov', 'Title')} sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                                        <SortHeader k="category" label={t('Kategorija', 'Category')} sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                                        <SortHeader k="status" label="Status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                                        <SortHeader k="created_at" label={t('Učitano', 'Uploaded')} sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
                                        <th>AI {t('sažetak', 'summary')}</th>
                                        <th>{t('Akcije', 'Actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDocs.length === 0 ? (
                                        <tr><td colSpan={7} style={{ textAlign: 'center', color: 'rgba(0,0,0,0.4)', padding: '1.2rem', fontStyle: 'italic' }}>
                                            {t('Nema dokumenata u ovom prikazu.', 'No documents in this view.')}
                                        </td></tr>
                                    ) : null}
                                    {filteredDocs.map((d) => {
                                        const onDocDoc = !!d.docdoc_document_uuid;
                                        const statusColor = d.status === 'ready' ? '#1f7a36' : d.status === 'failed' ? '#b14040' : d.status === 'processing' ? 'var(--ad-acc)' : '#6b7280';
                                        const statusIcon = d.status === 'ready' && onDocDoc ? '☁️'
                                            : d.status === 'failed' ? '⚠'
                                            : d.status === 'processing' ? '⏳'
                                            : '⏳';
                                        const statusLabel = d.status === 'ready' && onDocDoc ? t('na DocDoc-u', 'on DocDoc')
                                            : d.status === 'failed' ? t('greška', 'failed')
                                            : d.status === 'processing' ? t('obrađuje se', 'processing')
                                            : t('čeka', 'pending');
                                        return (
                                            <tr key={d.id} style={selectedIds.has(d.id) ? { background: 'rgba(254,243,199,0.4)' } : undefined}>
                                                <td><input type="checkbox" checked={selectedIds.has(d.id)} onChange={() => toggleSelected(d.id)} /></td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                        <span title={statusLabel} style={{ fontSize: '1rem', flexShrink: 0 }}>{statusIcon}</span>
                                                        {onDocDoc || d.is_circular ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => openDoc(d)}
                                                                style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer', textAlign: 'left', fontWeight: 600, color: 'var(--ad-ink)', textDecoration: 'underline', textDecorationStyle: 'dotted', textDecorationColor: 'rgba(var(--ad-soft-rgb),0.5)' }}
                                                                title={d.is_circular ? t('Pregled okružnice (view-only)', 'View circular (view-only)') : t('Klik za pregled PDF-a iz DocDoc-a', 'Click to preview PDF from DocDoc')}
                                                            >{d.is_circular ? '📨 ' : ''}{d.title}</button>
                                                        ) : (
                                                            <span style={{ fontWeight: 600 }}>{d.title}</span>
                                                        )}
                                                    </div>
                                                    <div className="muted" style={{ fontSize: '0.78rem', marginLeft: 22 }}>{d.original_filename}</div>
                                                    {d.is_circular && (d.circular_number || d.approved_by) ? (
                                                        <div style={{ marginLeft: 22, marginTop: 2, fontSize: '0.72rem', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                            {d.circular_number ? <span style={{ fontFamily: 'ui-monospace, monospace', color: 'var(--ad-acc-3)', fontWeight: 700 }}>{d.circular_number}</span> : null}
                                                            {d.attachment_number ? <span style={{ color: 'var(--ad-acc-3)' }}>📎 {d.attachment_number}</span> : null}
                                                            {d.approved_by ? <span style={{ color: '#1f7a36' }}>✓ {t('odobrio', 'approved by')}: {d.approved_by}{d.approved_at ? ` · ${new Date(d.approved_at).toLocaleDateString('hr-HR')}` : ''}</span> : null}
                                                        </div>
                                                    ) : null}
                                                </td>
                                                <td>{d.category_name ?? <span className="muted">—</span>}</td>
                                                <td><span style={{ color: statusColor, fontWeight: 600 }}>● {d.status}</span></td>
                                                <td style={{ fontSize: '0.82rem' }}>{d.created_at ? new Date(d.created_at).toLocaleDateString('hr-HR') : '—'}</td>
                                                <td style={{ maxWidth: '22rem', fontSize: '0.78rem', color: 'rgba(0,0,0,0.7)' }}>
                                                    {d.ai_summary ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setSummaryDoc(d)}
                                                            style={{
                                                                background: 'transparent', border: 0, padding: 0,
                                                                textAlign: 'left', cursor: 'pointer',
                                                                fontSize: '0.78rem', color: 'rgba(0,0,0,0.75)',
                                                                display: 'block', width: '100%',
                                                            }}
                                                            title={t('Klik za cijeli AI sažetak', 'Click for full AI summary')}
                                                        >
                                                            <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>🤖 {d.ai_summary.slice(0, 180)}{d.ai_summary.length > 180 ? '…' : ''}</span>
                                                        </button>
                                                    ) : <span className="muted">—</span>}
                                                </td>
                                                <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                    {onDocDoc || d.is_circular ? <button type="button" className="secondary compact" onClick={() => openDoc(d)} title={d.is_circular ? t('Pregled okružnice (view-only)', 'View circular (view-only)') : t('Pregled PDF-a iz DocDoc-a', 'Preview PDF from DocDoc')}>👁</button> : null}
                                                    <button type="button" className="secondary compact" onClick={() => setEditDoc(d)} title={t('Uredi metapodatke', 'Edit metadata')}>✎</button>
                                                    <button type="button" className="secondary compact" style={{ color: '#b14040' }} onClick={() => void deleteDoc(d.id)} title={t('Obriši', 'Delete')}>🗑</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : null}

            {/* AI search tab */}
            {tab === 'search' ? (
                <div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                        <input
                            type="search"
                            value={searchQ}
                            onChange={(e) => setSearchQ(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') void runSearch(); }}
                            placeholder={t('Pretraži po svim tvrtkama kojima imaš pristup…', 'Search across all companies you can access…')}
                            style={{ flex: 1, minWidth: 280, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.35)', fontSize: '0.92rem' }}
                        />
                        <button type="button" className="compact" disabled={searchBusy} onClick={() => void runSearch()}>
                            {searchBusy ? '⏳' : '🔍'} {t('Pretraži', 'Search')}
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                        {searchHits.map((h) => (
                            <div key={h.id} style={{ border: '1px solid rgba(var(--ad-soft-rgb),0.22)', borderRadius: 10, padding: '0.8rem 1rem', background: '#fffdf6' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                                    <strong style={{ color: 'var(--ad-ink)' }}>{h.title}</strong>
                                    <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)' }}>{h.company_code ? `${h.company_code} · ` : ''}{h.company_name}{h.category_name ? ` · ${h.category_name}` : ''}</span>
                                </div>
                                {h.snippet ? <p style={{ margin: '4px 0', fontSize: '0.84rem' }}>{h.snippet}…</p> : null}
                                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                    {h.has_docdoc ? <button type="button" className="secondary compact" onClick={() => viewDoc(h.id)}>👁 {t('Pregled', 'View')}</button> : null}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* Modal: Categories manager */}
            {showCategoriesModal && companyId ? (
                <CategoriesManagerModal
                    token={token}
                    locale={locale}
                    companyId={companyId}
                    categories={categories}
                    onClose={() => setShowCategoriesModal(false)}
                    onChange={async () => { await load(); }}
                />
            ) : null}

            {/* Modal: New document */}
            {showNewDocModal && companyId ? (
                <NewDocumentModal
                    token={token}
                    locale={locale}
                    companyId={companyId}
                    categories={categories}
                    onClose={() => setShowNewDocModal(false)}
                    onUploaded={async () => { setShowNewDocModal(false); await load(); }}
                />
            ) : null}

            {/* Modal: AI summary */}
            {summaryDoc ? (
                <div className="modal-backdrop" onClick={() => setSummaryDoc(null)}>
                    <div onClick={(e) => e.stopPropagation()} style={{
                        width: 'min(720px, 96vw)', maxHeight: '90vh', overflowY: 'auto',
                        background: '#fffdf6', borderRadius: 16,
                        boxShadow: '0 20px 60px rgba(107,79,28,0.28), 0 0 0 1px rgba(var(--ad-soft-rgb),0.18)',
                    }}>
                        <div style={{ background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', color: '#fffbeb', padding: '1rem 1.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <p style={{ margin: 0, fontSize: '0.7rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>🤖 AI {t('sažetak', 'summary')}</p>
                                <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.3rem' }}>{summaryDoc.title}</h2>
                                <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'rgba(255,251,235,0.78)' }}>{summaryDoc.original_filename}{summaryDoc.category_name ? ` · ${summaryDoc.category_name}` : ''}</p>
                            </div>
                            <button type="button" onClick={() => setSummaryDoc(null)} style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.6rem', cursor: 'pointer' }}>×</button>
                        </div>
                        <div style={{ padding: '1.4rem 1.6rem', whiteSpace: 'pre-wrap', fontSize: '0.95rem', lineHeight: 1.6, color: '#1f2937' }}>
                            {summaryDoc.ai_summary}
                        </div>
                        <div style={{ padding: '0.8rem 1.6rem 1.2rem', borderTop: '1px solid rgba(var(--ad-soft-rgb),0.18)', display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                            {summaryDoc.docdoc_document_uuid ? (
                                <button type="button" className="secondary compact" onClick={() => { viewDoc(summaryDoc.id); }}>👁 {t('Pregled originala', 'View original')}</button>
                            ) : null}
                            <button type="button" className="secondary compact" onClick={() => setSummaryDoc(null)}>{t('Zatvori', 'Close')}</button>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* Modal: Edit document */}
            {editDoc ? (
                <EditDocumentModal
                    token={token}
                    locale={locale}
                    doc={editDoc}
                    categories={categories}
                    onClose={() => setEditDoc(null)}
                    onSaved={async () => { setEditDoc(null); await load(); }}
                />
            ) : null}

            {/* Lightbox: okružnica view-only (PNG stranice, bez skidanja) */}
            {circularPreview ? (
                <CircularDocLightbox token={token} locale={locale} doc={circularPreview} onClose={() => setCircularPreview(null)} />
            ) : null}
        </section>
    );
}

/**
 * View-only pregled okružnice u m.31: PNG stranice (bez skidanja). PNG endpointi traže auth header
 * (img src ne može), pa svaku stranicu dohvaćamo kao blob s Bearer tokenom. Skidanje (ovlašteni) → DocDoc.
 */
function CircularDocLightbox({ token, locale, doc, onClose }: { token: string; locale: 'hr' | 'en'; doc: DocumentRow; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [pages, setPages] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');

    useEffect(() => {
        let urls: string[] = [];
        let cancelled = false;
        (async () => {
            try {
                const info = await fetch(`/api/documentation/documents/${doc.id}/circular-pdf/info`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json());
                if (!info.has_pdf) { if (!cancelled) { setErr(t('Pregled nije dostupan.', 'Preview unavailable.')); setLoading(false); } return; }
                const out: string[] = [];
                for (let i = 1; i <= (info.pages || 1); i++) {
                    const res = await fetch(`/api/documentation/documents/${doc.id}/circular-pdf/page/${i}`, { headers: { Authorization: `Bearer ${token}` } });
                    if (!res.ok) break;
                    out.push(URL.createObjectURL(await res.blob()));
                }
                urls = out;
                if (!cancelled) { setPages(out); setLoading(false); }
            } catch { if (!cancelled) { setErr(t('Greška pri učitavanju pregleda.', 'Preview load error.')); setLoading(false); } }
        })();
        return () => { cancelled = true; urls.forEach((u) => URL.revokeObjectURL(u)); };
    }, [doc.id, token]);

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ position: 'sticky', top: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: 'rgba(0,0,0,0.6)' }}>
                <span style={{ color: '#fff', fontWeight: 700 }}>📨 {doc.title} <span style={{ fontWeight: 400, opacity: 0.7, fontSize: '0.8rem' }}>· {t('view-only — skidanje preko DocDoc-a', 'view-only — download via DocDoc')}</span></span>
                <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer', padding: '6px 12px', fontSize: 16 }}>✕</button>
            </div>
            <div onContextMenu={(e) => e.preventDefault()} style={{ overflow: 'auto', flex: 1, textAlign: 'center', padding: 16 }}>
                {loading ? <p style={{ color: '#ddd' }}>{t('Učitavam pregled…', 'Loading preview…')}</p>
                    : err ? <p style={{ color: '#f88' }}>{err}</p>
                    : pages.map((src, i) => <img key={i} src={src} alt={`p${i + 1}`} onContextMenu={(e) => e.preventDefault()} style={{ width: '100%', maxWidth: 880, display: 'block', margin: '0 auto 12px', boxShadow: '0 2px 16px rgba(0,0,0,0.5)', background: '#fff' }} />)}
            </div>
        </div>
    );
}

function SortHeader({ k, label, sortKey, sortDir, onClick }: { k: SortKey; label: string; sortKey: SortKey; sortDir: 'asc' | 'desc'; onClick: (k: SortKey) => void }) {
    const active = sortKey === k;
    return (
        <th onClick={() => onClick(k)} style={{ cursor: 'pointer', userSelect: 'none' }}>
            {label} <span style={{ opacity: active ? 1 : 0.25, fontSize: '0.74rem' }}>{active ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}</span>
        </th>
    );
}

function categoryPathLabel(all: CategoryRow[], id: number): string {
    const byId = new Map(all.map((c) => [c.id, c]));
    const chain: string[] = [];
    let cur: CategoryRow | undefined = byId.get(id);
    while (cur) {
        chain.unshift(cur.name);
        cur = cur.parent_id !== null ? byId.get(cur.parent_id) : undefined;
    }
    return chain.join(' › ');
}

function buildTree(all: CategoryRow[], parent: number | null): CategoryRow[] {
    return all.filter((c) => (c.parent_id ?? null) === parent);
}

function CategoryTreePanel({ categories, selected, onSelect, locale, docsAllCount }: {
    categories: CategoryRow[];
    selected: number | 'all' | 'uncategorized';
    onSelect: (v: number | 'all' | 'uncategorized') => void;
    locale: 'hr' | 'en';
    docsAllCount: number;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

    function toggleExpand(id: number) {
        const next = new Set(expanded);
        if (next.has(id)) next.delete(id); else next.add(id);
        setExpanded(next);
    }

    // Rolled-up count: dokumenti u kategoriji + svim pod-kategorijama (1 dok = 1 kategorija → bez dvostrukog brojanja).
    function rollupCount(id: number): number {
        const self = categories.find((c) => c.id === id)?.doc_count ?? 0;
        return self + buildTree(categories, id).reduce((s, ch) => s + rollupCount(ch.id), 0);
    }

    function renderNode(node: CategoryRow, level: number): React.ReactNode {
        const children = buildTree(categories, node.id);
        const hasChildren = children.length > 0;
        const isOpen = expanded.has(node.id);
        const isSelected = selected === node.id;
        return (
            <div key={node.id}>
                <div
                    onClick={() => onSelect(node.id)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '4px 6px',
                        paddingLeft: 6 + level * 14,
                        background: isSelected ? 'linear-gradient(135deg, #fef3c7, rgba(254,243,199,0.5))' : 'transparent',
                        borderLeft: isSelected ? '3px solid var(--ad-acc)' : '3px solid transparent',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: '0.86rem',
                        color: 'var(--ad-ink)',
                    }}
                >
                    {hasChildren ? (
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggleExpand(node.id); }} style={{ background: 'transparent', border: 0, cursor: 'pointer', padding: '0 4px', color: 'var(--ad-ink)' }}>
                            {isOpen ? '▾' : '▸'}
                        </button>
                    ) : <span style={{ width: 16, display: 'inline-block' }} />}
                    <span style={{ flex: 1, fontWeight: isSelected ? 700 : 500 }}>{node.name}</span>
                    <span style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.5)' }} title={hasChildren ? `${node.doc_count} direktno · ${rollupCount(node.id)} ukupno` : undefined}>{rollupCount(node.id)}</span>
                </div>
                {hasChildren && isOpen ? children.map((c) => renderNode(c, level + 1)) : null}
            </div>
        );
    }

    const roots = buildTree(categories, null);

    return (
        <aside style={{
            border: '1px solid rgba(var(--ad-soft-rgb),0.22)',
            borderRadius: 10,
            padding: '0.6rem 0.4rem',
            background: '#fffdf6',
            position: 'sticky',
            top: 10,
        }}>
            <h4 style={{ margin: '0 0 0.4rem 0.4rem', fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ad-ink)' }}>
                📁 {t('Kategorije', 'Categories')}
            </h4>
            <button
                type="button"
                onClick={() => onSelect('all')}
                style={{
                    display: 'flex', justifyContent: 'space-between', width: '100%',
                    padding: '4px 8px', borderRadius: 6,
                    background: selected === 'all' ? 'linear-gradient(135deg, #fef3c7, rgba(254,243,199,0.5))' : 'transparent',
                    borderLeft: selected === 'all' ? '3px solid var(--ad-acc)' : '3px solid transparent',
                    border: 'none', cursor: 'pointer',
                    fontSize: '0.86rem', color: 'var(--ad-ink)', fontWeight: selected === 'all' ? 700 : 500,
                }}
            >
                <span>📂 {t('Sve', 'All')}</span>
                <span style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.5)' }}>{docsAllCount}</span>
            </button>
            <button
                type="button"
                onClick={() => onSelect('uncategorized')}
                style={{
                    display: 'flex', justifyContent: 'space-between', width: '100%',
                    padding: '4px 8px', borderRadius: 6, marginBottom: 4,
                    background: selected === 'uncategorized' ? 'linear-gradient(135deg, #fef3c7, rgba(254,243,199,0.5))' : 'transparent',
                    borderLeft: selected === 'uncategorized' ? '3px solid var(--ad-acc)' : '3px solid transparent',
                    border: 'none', cursor: 'pointer',
                    fontSize: '0.86rem', color: 'var(--ad-ink)', fontWeight: selected === 'uncategorized' ? 700 : 500,
                }}
            >
                <span>📥 {t('Bez kategorije', 'Uncategorized')}</span>
            </button>
            {roots.length === 0 ? (
                <p className="muted" style={{ margin: '0.6rem 0.4rem', fontSize: '0.78rem' }}>{t('Nema kategorija. Kreiraj kroz "📁 Kategorije" gumb iznad.', 'No categories yet.')}</p>
            ) : roots.map((r) => renderNode(r, 0))}
        </aside>
    );
}

function CategoriesManagerModal({ token, locale, companyId, categories, onClose, onChange }: {
    token: string;
    locale: 'hr' | 'en';
    companyId: number;
    categories: CategoryRow[];
    onClose: () => void;
    onChange: () => Promise<void> | void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [newName, setNewName] = useState('');
    const [newParent, setNewParent] = useState<string>('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editName, setEditName] = useState('');
    const [editParent, setEditParent] = useState<string>('');
    const [err, setErr] = useState('');

    async function addNew() {
        if (! newName.trim()) return;
        setErr('');
        try {
            await mutate('/documentation/categories', token, 'POST', {
                company_id: companyId,
                name: newName.trim(),
                parent_id: newParent ? Number(newParent) : null,
            });
            setNewName(''); setNewParent('');
            await onChange();
        } catch (e) { setErr(e instanceof Error ? e.message : ''); }
    }

    async function saveEdit() {
        if (editingId == null) return;
        setErr('');
        try {
            await mutate(`/documentation/categories/${editingId}`, token, 'PATCH', {
                name: editName,
                parent_id: editParent === '' ? null : Number(editParent),
            });
            setEditingId(null);
            await onChange();
        } catch (e) { setErr(e instanceof Error ? e.message : ''); }
    }

    async function destroy(id: number, docCount: number) {
        if (docCount > 0) {
            alert(t(`Kategorija (ili pod-kategorija) sadrži ${docCount} dokumenata. Premjesti ih prije brisanja.`, `Category contains ${docCount} documents. Move them first.`));
            return;
        }
        if (! confirm(t('Obrisati kategoriju?', 'Delete category?'))) return;
        setErr('');
        try {
            await mutate(`/documentation/categories/${id}`, token, 'DELETE');
            await onChange();
        } catch (e) { setErr(e instanceof Error ? e.message : ''); }
    }

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: 'min(680px, 96vw)', maxHeight: '94vh', overflowY: 'auto',
                background: '#fffdf6', borderRadius: 16,
                boxShadow: '0 20px 60px rgba(107,79,28,0.28), 0 0 0 1px rgba(var(--ad-soft-rgb),0.18)',
            }}>
                <div style={{ background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', color: '#fffbeb', padding: '1rem 1.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.7rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>{t('Modul 31 · Dokumentacija', 'Module 31 · Documentation')}</p>
                        <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.3rem' }}>📁 {t('Upravljanje kategorijama', 'Manage categories')}</h2>
                    </div>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.6rem', cursor: 'pointer' }}>×</button>
                </div>
                <div style={{ padding: '1.2rem 1.4rem' }}>
                    {err ? <p className="error">{err}</p> : null}
                    <h4 style={{ marginTop: 0 }}>{t('Nova kategorija', 'New category')}</h4>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '1rem' }}>
                        <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('Naziv', 'Name')} style={{ flex: 1, minWidth: 160, padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.35)' }} />
                        <select value={newParent} onChange={(e) => setNewParent(e.target.value)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.35)' }}>
                            <option value="">{t('— Bez roditelja (root)', '— No parent (root)')}</option>
                            {categories.map((c) => <option key={c.id} value={c.id}>{categoryPathLabel(categories, c.id)}</option>)}
                        </select>
                        <button type="button" className="compact" onClick={() => void addNew()}>+ {t('Dodaj', 'Add')}</button>
                    </div>

                    <h4>{t('Postojeće', 'Existing')} ({categories.length})</h4>
                    {categories.length === 0 ? <p className="muted">{t('Nema kategorija.', 'No categories.')}</p> : null}
                    <table className="admin-user-table" style={{ width: '100%' }}>
                        <thead>
                            <tr><th>{t('Naziv', 'Name')}</th><th>{t('Roditelj', 'Parent')}</th><th style={{ textAlign: 'right' }}>{t('Dok.', 'Docs')}</th><th /></tr>
                        </thead>
                        <tbody>
                            {categories.map((c) => (
                                <tr key={c.id}>
                                    <td>
                                        {editingId === c.id ? (
                                            <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} />
                                        ) : (
                                            <span style={{ fontWeight: 600 }}>{c.name}</span>
                                        )}
                                    </td>
                                    <td>
                                        {editingId === c.id ? (
                                            <select value={editParent} onChange={(e) => setEditParent(e.target.value)}>
                                                <option value="">— root —</option>
                                                {categories.filter((p) => p.id !== c.id).map((p) => <option key={p.id} value={p.id}>{categoryPathLabel(categories, p.id)}</option>)}
                                            </select>
                                        ) : (
                                            <span className="muted">{c.parent_id ? categoryPathLabel(categories, c.parent_id) : '—'}</span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>{c.doc_count}</td>
                                    <td style={{ display: 'flex', gap: 4 }}>
                                        {editingId === c.id ? (
                                            <>
                                                <button type="button" className="compact" onClick={() => void saveEdit()}>💾</button>
                                                <button type="button" className="secondary compact" onClick={() => setEditingId(null)}>×</button>
                                            </>
                                        ) : (
                                            <>
                                                <button type="button" className="secondary compact" onClick={() => { setEditingId(c.id); setEditName(c.name); setEditParent(c.parent_id ? String(c.parent_id) : ''); }}>✎</button>
                                                <button type="button" className="secondary compact" style={{ color: '#b14040', opacity: c.doc_count > 0 ? 0.5 : 1 }} disabled={c.doc_count > 0} onClick={() => void destroy(c.id, c.doc_count)} title={c.doc_count > 0 ? t('Kategorija sadrži dokumente', 'Has docs') : ''}>🗑</button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ marginTop: '1rem', textAlign: 'right' }}>
                        <button type="button" className="secondary compact" onClick={onClose}>{t('Zatvori', 'Close')}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function NewDocumentModal({ token, locale, companyId, categories, onClose, onUploaded }: {
    token: string;
    locale: 'hr' | 'en';
    companyId: number;
    categories: CategoryRow[];
    onClose: () => void;
    onUploaded: () => Promise<void> | void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [title, setTitle] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState('');

    async function submit(e: FormEvent) {
        e.preventDefault();
        if (! title.trim() || ! file) return;
        setErr(''); setUploading(true);
        try {
            const fd = new FormData();
            fd.set('company_id', String(companyId));
            fd.set('title', title.trim());
            fd.set('file', file);
            if (categoryId) fd.set('category_id', categoryId);
            await uploadFile<{ id: number }>('/documentation/documents', token, fd);
            await onUploaded();
        } catch (e) { setErr(e instanceof Error ? e.message : ''); }
        finally { setUploading(false); }
    }

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: 'min(640px, 96vw)', maxHeight: '94vh', overflowY: 'auto',
                background: '#fffdf6', borderRadius: 16,
                boxShadow: '0 20px 60px rgba(107,79,28,0.28), 0 0 0 1px rgba(var(--ad-soft-rgb),0.18)',
            }}>
                <div style={{ background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', color: '#fffbeb', padding: '1rem 1.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.7rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>{t('Modul 31 · Dokumentacija', 'Module 31 · Documentation')}</p>
                        <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.3rem' }}>📤 {t('Novi dokument', 'New document')}</h2>
                    </div>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.6rem', cursor: 'pointer' }}>×</button>
                </div>
                <form onSubmit={submit} style={{ padding: '1.2rem 1.4rem' }}>
                    {err ? <p className="error">{err}</p> : null}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '0.7rem' }}>
                        <label className="filter-label" style={{ margin: 0 }}>
                            {t('Naslov', 'Title')}
                            <input value={title} onChange={(e) => setTitle(e.target.value)} required />
                        </label>
                        <label className="filter-label" style={{ margin: 0 }}>
                            {t('Kategorija', 'Category')}
                            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                                <option value="">—</option>
                                {categories.map((c) => <option key={c.id} value={c.id}>{categoryPathLabel(categories, c.id)}</option>)}
                            </select>
                        </label>
                    </div>
                    <input ref={fileRef} type="file" style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                        onChange={(e) => setFile(e.target.files?.[0] ?? null)} accept=".pdf,.docx,.txt,.md,.xlsx" />
                    <div
                        role="button" tabIndex={0}
                        onClick={() => fileRef.current?.click()}
                        onDragOver={(ev) => { ev.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(ev) => { ev.preventDefault(); setDragOver(false); const f = ev.dataTransfer.files?.[0]; if (f) setFile(f); }}
                        style={{
                            border: dragOver ? '2.5px dashed var(--ad-acc)' : '2.5px dashed rgba(var(--ad-soft-rgb),0.45)',
                            background: dragOver ? 'rgba(var(--ad-acc-rgb),0.10)' : 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)',
                            borderRadius: 14, padding: '1.5rem', textAlign: 'center', cursor: 'pointer',
                            marginTop: '0.8rem',
                        }}
                    >
                        <div style={{ fontSize: '2.4rem' }}>⤓</div>
                        {file ? (
                            <div>
                                <strong>{file.name}</strong>
                                <div className="muted" style={{ fontSize: '0.78rem' }}>{(file.size / 1024 / 1024).toFixed(2)} MB · {t('klikni za zamjenu', 'click to replace')}</div>
                            </div>
                        ) : (
                            <div>
                                <strong>{t('Povuci datoteku ovdje', 'Drop file here')}</strong> {t('ili', 'or')} <span style={{ textDecoration: 'underline', color: 'var(--ad-ink)' }}>{t('odaberi s računala', 'browse')}</span>
                                <div className="muted" style={{ fontSize: '0.78rem' }}>{t('do 25 MB · PDF, DOCX, TXT, XLSX', 'up to 25 MB')}</div>
                            </div>
                        )}
                    </div>
                    <p className="muted" style={{ fontSize: '0.74rem', marginTop: 8 }}>
                        {t('Tok: BIS izvuče tekst → OpenAI sažetak → pošalje u DocDoc → obriše tmp. Original ostaje samo na DocDoc-u.', 'Pipeline: extract → AI summary → push to DocDoc → delete tmp.')}
                    </p>
                    <div style={{ display: 'flex', gap: 6, marginTop: '1rem', justifyContent: 'flex-end' }}>
                        <button type="submit" className="compact" disabled={uploading || !file || !title.trim()}>
                            {uploading ? '⏳ ' + t('Šaljem…', 'Pushing…') : '📤 ' + t('Učitaj i analiziraj', 'Upload & analyze')}
                        </button>
                        <button type="button" className="secondary compact" onClick={onClose}>{t('Odustani', 'Cancel')}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function EditDocumentModal({ token, locale, doc, categories, onClose, onSaved }: {
    token: string;
    locale: 'hr' | 'en';
    doc: DocumentRow;
    categories: CategoryRow[];
    onClose: () => void;
    onSaved: () => Promise<void> | void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [title, setTitle] = useState(doc.title);
    const [categoryId, setCategoryId] = useState<string>(doc.category_id ? String(doc.category_id) : '');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    async function save(e: FormEvent) {
        e.preventDefault();
        setErr(''); setBusy(true);
        try {
            await mutate(`/documentation/documents/${doc.id}`, token, 'PATCH', {
                title,
                category_id: categoryId ? Number(categoryId) : null,
            });
            await onSaved();
        } catch (e) { setErr(e instanceof Error ? e.message : ''); }
        finally { setBusy(false); }
    }

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: 'min(560px, 96vw)',
                background: '#fffdf6', borderRadius: 16,
                boxShadow: '0 20px 60px rgba(107,79,28,0.28), 0 0 0 1px rgba(var(--ad-soft-rgb),0.18)',
            }}>
                <div style={{ background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', color: '#fffbeb', padding: '1rem 1.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.7rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>✎ {t('Uredi metapodatke', 'Edit metadata')}</p>
                        <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.2rem' }}>{doc.title}</h2>
                    </div>
                    <button type="button" onClick={onClose} style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.6rem', cursor: 'pointer' }}>×</button>
                </div>
                <form onSubmit={save} style={{ padding: '1.2rem 1.4rem' }}>
                    {err ? <p className="error">{err}</p> : null}
                    <label className="filter-label">
                        {t('Naslov', 'Title')}
                        <input value={title} onChange={(e) => setTitle(e.target.value)} required />
                    </label>
                    <label className="filter-label">
                        {t('Kategorija', 'Category')}
                        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                            <option value="">—</option>
                            {categories.map((c) => <option key={c.id} value={c.id}>{categoryPathLabel(categories, c.id)}</option>)}
                        </select>
                    </label>
                    <p className="muted" style={{ fontSize: '0.74rem', marginTop: 8 }}>
                        {t('Original file je na DocDoc-u; ovdje uređuješ samo metapodatke.', 'Original file lives on DocDoc; only metadata is editable here.')}
                    </p>
                    <div style={{ display: 'flex', gap: 6, marginTop: '1rem', justifyContent: 'flex-end' }}>
                        <button type="submit" className="compact" disabled={busy}>💾 {t('Spremi', 'Save')}</button>
                        <button type="button" className="secondary compact" onClick={onClose}>{t('Odustani', 'Cancel')}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
